import type { ComponentType, ReactNode, SVGProps } from 'react';

/*
 * Detail-view header. Renders a large resource icon next to the resource
 * title and a row of metadata + status underneath. Follows tokens.md §2 for
 * the affordance language and §2.3.1 for the property-status mapping.
 */

type StatusTone = 'aktiv' | 'verhandelt' | 'reserviert' | 'offmarket' | 'verkauft';

const statusClass: Record<StatusTone, string> = {
  aktiv: 'bg-success-50 text-success-700',
  verhandelt: 'bg-[var(--status-verhandelt-bg)] text-[var(--status-verhandelt-fg)]',
  reserviert: 'bg-warning-100 text-[var(--status-reserviert-fg)]',
  offmarket: 'bg-purple-100 text-purple-600',
  verkauft: 'bg-subtle text-text-tertiary',
};

type Props = {
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
  readonly title: string;
  readonly meta?: ReactNode;
  readonly status?: { tone: StatusTone; label: string };
};

export function ResourceHeader({ icon: Icon, title, meta, status }: Props) {
  return (
    <header className="flex items-start gap-4 px-6 py-6">
      <span
        aria-hidden
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary"
      >
        <Icon className="h-6 w-6" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-3">
          <h1 className="truncate text-3xl font-bold tracking-tight text-text-primary">{title}</h1>
          {status !== undefined && (
            <span
              className={`inline-flex items-center rounded-pill px-3 py-0.5 text-sm font-medium ${statusClass[status.tone]}`}
            >
              {status.label}
            </span>
          )}
        </div>
        {meta !== undefined && <div className="text-base text-text-tertiary">{meta}</div>}
      </div>
    </header>
  );
}
